
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Folder, FolderPlus, MoreHorizontal, Edit, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/components/ui/sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

// Define default categories that match entry types
const DEFAULT_CATEGORIES = [
  { id: "all", name: "All", system: true },
  { id: "Company", name: "Company", system: true },
  { id: "Program", name: "Programs", system: true },
  { id: "Contact", name: "People", system: true },
  { id: "Interview Prep", name: "Prep", system: true },
  { id: "Reflection", name: "Personal", system: true },
  { id: "General Note", name: "Notes", system: true },
];

type Category = {
  id: string;
  name: string;
  creator_id?: string;
  system?: boolean;
};

export const CategoryList = () => {
  const [categories, setCategories] = useState<Category[]>(DEFAULT_CATEGORIES);
  const [customCategories, setCustomCategories] = useState<Category[]>([]);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [editCategoryName, setEditCategoryName] = useState("");
  const [editCategoryId, setEditCategoryId] = useState<string | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  // Extract the current category from URL search params
  const searchParams = new URLSearchParams(location.search);
  const currentCategory = searchParams.get('category') || 'all';
  
  // Load custom categories
  useEffect(() => {
    if (user) {
      fetchCustomCategories();
    }
  }, [user]);
  
  const fetchCustomCategories = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('creator_id', user.id);
        
      if (error) {
        console.error("Error fetching categories:", error);
        toast.error("Failed to load custom categories");
        return;
      }
      
      if (data) {
        setCustomCategories(data);
      }
    } catch (error) {
      console.error("Error in fetchCustomCategories:", error);
    }
  };
  
  const handleCategoryClick = (categoryId: string) => {
    navigate(`/dashboard?category=${categoryId}`);
  };
  
  const handleAddCategory = async () => {
    if (!newCategoryName.trim() || !user) {
      toast.error("Please enter a category name");
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('categories')
        .insert([{
          name: newCategoryName,
          creator_id: user.id,
        }])
        .select();
        
      if (error) {
        toast.error("Failed to create category");
        console.error("Error creating category:", error);
      } else if (data) {
        toast.success("Category created");
        setCustomCategories([...customCategories, data[0]]);
        setNewCategoryName("");
        setIsAddDialogOpen(false);
      }
    } catch (error) {
      console.error("Error in handleAddCategory:", error);
    }
  };
  
  const handleEditCategory = async () => {
    if (!editCategoryName.trim() || !editCategoryId || !user) {
      toast.error("Please enter a category name");
      return;
    }
    
    try {
      const { error } = await supabase
        .from('categories')
        .update({ name: editCategoryName })
        .eq('id', editCategoryId)
        .eq('creator_id', user.id);
        
      if (error) {
        toast.error("Failed to update category");
        console.error("Error updating category:", error);
      } else {
        toast.success("Category updated");
        setCustomCategories(customCategories.map(cat => 
          cat.id === editCategoryId ? { ...cat, name: editCategoryName } : cat
        ));
        setEditCategoryName("");
        setEditCategoryId(null);
        setIsEditDialogOpen(false);
      }
    } catch (error) {
      console.error("Error in handleEditCategory:", error);
    }
  };
  
  const handleDeleteCategory = async () => {
    if (!categoryToDelete || !user) return;
    
    try {
      const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', categoryToDelete.id)
        .eq('creator_id', user.id);
        
      if (error) {
        toast.error("Failed to delete category");
        console.error("Error deleting category:", error);
      } else {
        toast.success("Category deleted");
        setCustomCategories(customCategories.filter(cat => cat.id !== categoryToDelete.id));
        
        // If the deleted category was active, navigate to 'all'
        if (currentCategory === categoryToDelete.id) {
          navigate('/dashboard?category=all');
        }
        
        setCategoryToDelete(null);
        setIsDeleteDialogOpen(false);
      }
    } catch (error) {
      console.error("Error in handleDeleteCategory:", error);
    }
  };
  
  const openEditDialog = (category: Category) => {
    setEditCategoryId(category.id);
    setEditCategoryName(category.name);
    setIsEditDialogOpen(true);
  };
  
  const openDeleteDialog = (category: Category) => {
    setCategoryToDelete(category);
    setIsDeleteDialogOpen(true);
  };
  
  // Combine default and custom categories for display
  const allCategories = [...categories, ...customCategories];
  
  return (
    <div className="space-y-1">
      {allCategories.map((category) => (
        <div key={category.id} className="flex items-center group">
          <Button
            variant="ghost"
            className={cn(
              "w-full justify-start gap-2 text-sm",
              currentCategory === category.id && "bg-accent text-accent-foreground"
            )}
            onClick={() => handleCategoryClick(category.id)}
          >
            <Folder className="h-4 w-4" />
            <span>{category.name}</span>
          </Button>
          
          {!category.system && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 opacity-0 group-hover:opacity-100"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => openEditDialog(category)}>
                  <Edit className="mr-2 h-4 w-4" />
                  Rename
                </DropdownMenuItem>
                <DropdownMenuItem 
                  className="text-red-600" 
                  onClick={() => openDeleteDialog(category)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      ))}
      
      {/* Add category button */}
      <Button
        variant="ghost"
        className="w-full justify-start gap-2 text-sm"
        onClick={() => setIsAddDialogOpen(true)}
      >
        <FolderPlus className="h-4 w-4" />
        <span>Add Category</span>
      </Button>
      
      {/* Add Category Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Category</DialogTitle>
            <DialogDescription>
              Create a new category for organizing your entries
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="name">Category Name</Label>
              <Input
                id="name"
                placeholder="Enter category name"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddCategory}>
              Add Category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Category Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Category Name</Label>
              <Input
                id="edit-name"
                placeholder="Enter category name"
                value={editCategoryName}
                onChange={(e) => setEditCategoryName(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditCategory}>
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Category Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Category</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{categoryToDelete?.name}"? 
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteCategory}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
